<template>
  <div class="container-fluid py-4">
    <!-- UPDATED: Added responsive flex classes and gy-3 for vertical spacing on mobile -->
    <div class="row mb-4 align-items-center gy-3">
      <div class="col-md-5 col-12">
        <h4 class="text-dark fw-900 mb-1">Trash / Deleted Content</h4>
        <p class="text-muted small fw-bold mb-0">Items here can be restored to the library or permanently deleted.</p>
      </div>
      
      <!-- NEW: BULK ACTIONS SECTION -->
      <div v-if="selectedIds.length > 0" class="col-md-3 col-12 d-flex gap-2 align-items-center justify-content-center">
          <button @click="bulkRestore" class="btn btn-success btn-sm mb-0 shadow-sm flex-grow-1">
              <i class="fas fa-undo me-1"></i> RESTORE ({{ selectedIds.length }})
          </button>
          <button @click="bulkPurge" class="btn btn-danger btn-sm mb-0 shadow-sm flex-grow-1">
              <i class="fas fa-fire me-1"></i> Delete ALL
          </button>
      </div>

      <!-- UPDATED: Added flex-wrap and gap for mobile layout -->
      <div class="col-md-4 col-12 d-flex flex-wrap justify-content-start justify-content-md-end align-items-center gap-2" :class="{'col-md-4': selectedIds.length === 0, 'col-md-4': selectedIds.length > 0}">
        
        <!-- NEW: SELECT ALL CHECKBOX -->
        <div v-if="filteredDeletedList.length > 0" class="form-check me-2 mb-0">
          <input class="form-check-input" type="checkbox" id="selectAllTrash" @change="toggleSelectAll($event)">
          <label class="form-check-label fw-bold text-muted text-xxs mb-0" for="selectAllTrash" style="cursor:pointer">SELECT ALL</label>
        </div>

        <!-- DOWNLOAD ZIP BUTTON - Synced with Theme -->
        <button 
          @click="downloadZip" 
          class="btn btn-theme-primary btn-sm mb-0 d-flex align-items-center shadow-sm flex-grow-1 flex-md-grow-0 justify-content-center"
          :disabled="filteredDeletedList.length === 0"
        >
          <i class="fas fa-file-archive me-2"></i> DOWNLOAD ZIP
        </button>

        <div class="d-flex align-items-center bg-white p-2 rounded shadow-sm border flex-grow-1 flex-md-grow-0">
          <label class="text-xxs fw-900 text-uppercase text-muted me-2 mb-0">Filter Month:</label>
          <input 
            type="month" 
            v-model="selectedMonth" 
            class="form-control form-control-sm border-0 fw-bold p-0"
            style="cursor: pointer; font-size: 12px; background: transparent;"
          />
          <button 
            v-if="selectedMonth" 
            @click="selectedMonth = ''" 
            class="btn btn-link btn-sm text-danger p-0 ms-2 mb-0" 
            title="Clear Filter"
          >
            <i class="fas fa-times-circle"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- UPDATED: Added col-12 for full width on mobile devices -->
      <div v-for="item in filteredDeletedList" :key="item.id" class="col-12 col-md-6 col-lg-4 col-xl-3 mb-4">
        <div class="card h-100 border-0 shadow-sm overflow-hidden trash-card" :class="{'selected-card': selectedIds.includes(item.id)}">
          
          <!-- MEDIA BOX -->
          <div class="video-box bg-dark" style="height: 180px; position: relative;">
            
            <!-- NEW: INDIVIDUAL SELECTION CHECKBOX -->
            <div class="selection-overlay-container">
              <input type="checkbox" :value="item.id" v-model="selectedIds" class="content-select-checkbox">
            </div>

            <video 
              v-if="item.file_type === 'video'" 
              :src="`${apiBaseUrl}/${item.file_path}`" 
              class="w-100 h-100"
              muted 
              playsinline
              controls
            ></video>
            <img 
              v-else 
              :src="`${apiBaseUrl}/${item.file_path}`" 
              class="w-100 h-100 object-fit-cover" 
            />
          </div>

          <div class="card-body p-3">
            <h6 class="text-sm fw-900 mb-2 text-truncate text-dark" :title="item.name">{{ item.name }}</h6>
            
            <div class="bg-light p-2 rounded mb-3 border">
              <div class="text-xxs text-uppercase fw-bold text-muted mb-1">
                Deleted By: <span class="text-dark fw-900 ms-1">{{ item.deleter?.name || 'Admin' }}</span>
              </div>
              <div class="text-xxs text-uppercase fw-bold text-muted">
                Date: <span class="text-dark ms-1">{{ formatDate(item.deleted_at) }}</span>
              </div>
            </div>

            <div class="d-flex gap-2">
              <!-- Restore Button Synced with Theme -->
              <button class="btn btn-theme-primary btn-sm flex-grow-1 mb-0 fw-900" @click="restoreItem(item.id)">
                <i class="fas fa-undo me-1"></i> RESTORE
              </button>
              <button class="btn btn-outline-danger btn-sm mb-0" @click="purgeItem(item.id)" title="Delete Permanently">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="filteredDeletedList.length === 0" class="col-12 text-center py-5 text-muted">
        <i class="fas fa-trash-restore fa-3x mb-3 opacity-20"></i>
        <p v-if="selectedMonth" class="fw-bold">No items found for the selected month.</p>
        <p v-else class="fw-bold">Trash is empty.</p>
        <button v-if="selectedMonth" @click="selectedMonth = ''" class="btn btn-link btn-sm text-theme fw-900">Show all deleted items</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  data() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    
    return {
      deletedList: [],
      selectedMonth: `${currentYear}-${currentMonth}`, 
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL,
      selectedIds: [] // NEW: STORAGE FOR SELECTED CONTENT
    }
  },
  computed: {
    filteredDeletedList() {
      if (!this.selectedMonth) return this.deletedList;
      const [year, month] = this.selectedMonth.split('-');
      return this.deletedList.filter(item => {
        if (!item.deleted_at) return false;
        const date = new Date(item.deleted_at);
        const itemYear = date.getFullYear().toString();
        const itemMonth = (date.getMonth() + 1).toString().padStart(2, '0');
        return itemYear === year && itemMonth === month;
      });
    }
  },
  mounted() { this.fetchDeleted(); },
  methods: {
    // NEW: TOGGLE SELECT ALL
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.filteredDeletedList.map(item => item.id);
      } else {
        this.selectedIds = [];
      }
    },

    // NEW: BULK RESTORE
    async bulkRestore() {
      Swal.fire({
        title: 'Restore items?',
        text: `Are you sure you want to restore ${this.selectedIds.length} items to the library?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Restore All',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          const token = localStorage.getItem('token');
          try {
            await axios.post(`${this.apiBaseUrl}/v1/admin/content/bulk-restore`, {
              ids: this.selectedIds
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            this.selectedIds = [];
            this.fetchDeleted();
            Swal.fire('Restored!', 'Items have been returned to library.', 'success');
          } catch (error) {
            Swal.fire('Error', 'Failed to restore items.', 'error');
          }
        }
      });
    },

    // NEW: BULK PURGE
    async bulkPurge() {
      Swal.fire({
        title: 'Purge Selected?',
        text: `This will permanently delete ${this.selectedIds.length} items. This cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, Purge Permanently',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          const token = localStorage.getItem('token');
          try {
            await axios.post(`${this.apiBaseUrl}/v1/admin/content/bulk-purge`, {
              ids: this.selectedIds
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            this.selectedIds = [];
            this.fetchDeleted();
            Swal.fire('Purged!', 'Selected items deleted permanently.', 'success');
          } catch (error) {
            Swal.fire('Error', 'Failed to purge items.', 'error');
          }
        }
      });
    },

    async fetchDeleted() {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/content/deleted`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.deletedList = res.data;
      } catch (error) {
        console.error("Error fetching deleted content:", error);
      }
    },
    async restoreItem(id) {
      Swal.fire({
        title: 'Restore item?',
        text: "Move this item back to library?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Restore'
      }).then(async (result) => {
        if (result.isConfirmed) {
          const token = localStorage.getItem('token');
          await axios.put(`${this.apiBaseUrl}/v1/admin/content/restore/${id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          this.fetchDeleted();
          Swal.fire('Restored!', 'Item is now in the library.', 'success');
        }
      });
    },
    async purgeItem(id) {
      Swal.fire({
        title: 'Delete Permanently?',
        text: "This action cannot be undone!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, Delete'
      }).then(async (result) => {
        if (result.isConfirmed) {
          const token = localStorage.getItem('token');
          await axios.delete(`${this.apiBaseUrl}/v1/admin/content/permanent/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          this.fetchDeleted();
          Swal.fire('Deleted!', 'Item has been removed forever.', 'success');
        }
      });
    },
    formatDate(d) { 
      if(!d) return '';
      return new Date(d).toLocaleString('en-GB', { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }); 
    },
    async downloadZip() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${this.apiBaseUrl}/v1/admin/content/download-deleted-zip`, {
          params: { month: this.selectedMonth },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' 
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename = this.selectedMonth ? `deleted_content_${this.selectedMonth}.zip` : 'deleted_content_all.zip';
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error("Error downloading ZIP:", error);
        Swal.fire('Error', 'Failed to download ZIP file.', 'error');
      }
    }
  }
}
</script>

<style scoped>
/* ==========================
   DYNAMIC THEME SYNC
========================== */
.btn-theme-primary {
  background-color: var(--bs-primary) !important;
  border: 1px solid var(--bs-primary) !important;
  color: white !important;
  font-weight: 900 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}
.btn-theme-primary:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

.text-theme {
  color: var(--bs-primary) !important;
}

/* ==========================
   LAYOUT & CARDS
========================== */
.fw-900 { font-weight: 900 !important; }

.trash-card {
  border-radius: 14px;
  transition: transform 0.2s ease;
  position: relative;
  border: 2px solid transparent !important;
}
.trash-card:hover {
  transform: translateY(-5px);
}

/* NEW: SELECTION STYLES */
.selected-card {
  border: 2px solid var(--bs-primary) !important;
  box-shadow: 0 0 15px rgba(var(--bs-primary-rgb), 0.3) !important;
}

.selection-overlay-container {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 100;
}

.content-select-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--bs-primary);
}

.video-box { 
  background: #000; 
  overflow: hidden; 
}

.object-fit-cover { object-fit: cover; }
.text-xxs { font-size: 10px; letter-spacing: 0.3px; }
.text-sm { font-size: 14px; }

input[type="month"]::-webkit-calendar-picker-indicator { cursor: pointer; }
input[type="month"]:focus { outline: none; box-shadow: none; }

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .container-fluid {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
  .row.mb-4 {
    margin-bottom: 1.5rem !important;
  }
  h4 {
    font-size: 1.25rem;
  }
}
</style>